import { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
// ANMERKUNG (Security-Check): `npm audit` meldet für 4.7.1 einen kritischen
// XSS-Fund in maplibre-gls internem HTML-Sanitizer. Ein Update auf 6.9.0
// wurde geprüft und bewusst wieder verworfen: die Lücke betrifft
// `Popup#setHTML()` u. Ä. — hier wird ausschließlich `setDOMContent()` mit
// React-gerendertem (automatisch escapetem) Inhalt verwendet, Attribution ist
// deaktiviert, der Kartenstil kommt von Amazon Location Service (vertrauens-
// würdig, nicht nutzergesteuert) — die Lücke ist in diesem Code nicht
// erreichbar. Version 6 bündelt seinen Web-Worker außerdem als separates
// `.mjs`, das Vite weder im Dev-Server noch im Produktions-Build als eigene
// Datei ausliefert (404 → SPA-Fallback liefert index.html → Worker startet
// nie → die Karte bleibt dauerhaft leer, ohne Konsolenfehler) — mit `vite
// preview` gegen den echten Produktions-Build verifiziert. Ein sauberes
// Update bräuchte zusätzliche Bundler-Konfiguration fürs Worker-Bundling;
// das lohnt sich nicht für eine Lücke, die uns ohnehin nicht betrifft.
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { holeLocationAuthHelper, kartenStilUrl } from '../lib/locationAuth';
import { GuennieIcon } from './GuennieIcon';
import { GuennieRating, farbeFuerGuennies } from './GuennieRating';

export interface KartenPunkt {
  id: string;
  name: string;
  ort: string;
  lat: number;
  lng: number;
  anzahlTests: number;
  avgGesamtGuennies?: number | null;
}

/**
 * (0, 0) liegt im Atlantik vor Westafrika ("Null Island") — für eine
 * Eisdiele in Deutschland/Österreich/der Schweiz nie eine echte Position,
 * sondern immer ein Datenfehler (z. B. Alt-Testdaten ohne Geokodierung).
 * Ein einziger solcher Punkt würde `fitBounds` quer über halb Europa/Afrika
 * aufziehen und die eigentlichen Marker unkenntlich klein machen — deshalb
 * hier herausfiltern, statt das im Aufrufer jedes Mal prüfen zu müssen.
 */
function gueltigePosition(p: KartenPunkt): boolean {
  return Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);
}

/**
 * Kartenansicht mit einem Günnie-Marker pro Eisdiele (Bauphase 6). Farbe des
 * Markers folgt derselben Stufenfarbe wie GuennieRating. Ein Klick öffnet ein
 * Popup mit Kurzinfo; ist `onAuswahl` gesetzt, zeigt das Popup zusätzlich
 * einen Button dafür (i. d. R. Navigation zur Eisdielen-Detailseite).
 *
 * Wird sowohl für die volle Kartenseite (viele Punkte, `onAuswahl` gesetzt)
 * als auch als eingebettete Mini-Karte auf der Eisdielen-Detailseite
 * verwendet (ein einzelner Punkt, kein `onAuswahl` nötig).
 */
export function EisdielenKarte({
  punkte,
  hoehe = 480,
  onAuswahl,
}: {
  punkte: KartenPunkt[];
  hoehe?: number;
  onAuswahl?: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRoots = useRef<Root[]>([]);
  const popupRoots = useRef<Root[]>([]);
  const onAuswahlRef = useRef(onAuswahl);
  onAuswahlRef.current = onAuswahl;

  useEffect(() => {
    if (!containerRef.current) return;
    let abgebrochen = false;

    const gueltig = punkte.filter(gueltigePosition);
    if (gueltig.length < punkte.length) {
      console.warn(
        `EisdielenKarte: ${punkte.length - gueltig.length} Eisdiele(n) ohne gültige Position übersprungen (lat/lng fehlt oder 0/0):`,
        punkte.filter((p) => !gueltigePosition(p)).map((p) => `${p.name} (${p.id})`),
      );
    }
    const erstePunkt = gueltig[0];

    holeLocationAuthHelper()
      .then((helper) => {
        if (abgebrochen || !containerRef.current) return;
        const map = new maplibregl.Map({
          container: containerRef.current,
          style: kartenStilUrl(),
          // Fallback-Zentrum: Deutschland-Mitte, falls (noch) keine gültigen Punkte da sind.
          center: erstePunkt ? [erstePunkt.lng, erstePunkt.lat] : [10.45, 51.16],
          zoom: erstePunkt ? 12 : 5,
          transformRequest: helper.getMapAuthenticationOptions().transformRequest,
          attributionControl: false,
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

        map.on('load', () => {
          if (abgebrochen) return;
          const bounds = new maplibregl.LngLatBounds();

          for (const p of gueltig) {
            const iconEl = document.createElement('div');
            iconEl.style.cursor = 'pointer';
            iconEl.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,.35))';
            const iconRoot = createRoot(iconEl);
            iconRoot.render(<GuennieIcon gefuellt groesse={30} farbe={farbeFuerGuennies(p.avgGesamtGuennies)} titel={p.name} />);
            markerRoots.current.push(iconRoot);

            const popupEl = document.createElement('div');
            const popupRoot = createRoot(popupEl);
            popupRoot.render(
              <PopupInhalt punkt={p} onAnsehen={onAuswahlRef.current ? () => onAuswahlRef.current?.(p.id) : undefined} />,
            );
            popupRoots.current.push(popupRoot);

            new maplibregl.Marker({ element: iconEl, anchor: 'bottom' })
              .setLngLat([p.lng, p.lat])
              .setPopup(new maplibregl.Popup({ offset: 22, closeButton: false, maxWidth: '16rem' }).setDOMContent(popupEl))
              .addTo(map);
            bounds.extend([p.lng, p.lat]);
          }

          if (gueltig.length > 1) {
            map.fitBounds(bounds, { padding: 56, maxZoom: 14, duration: 0 });
          } else if (erstePunkt) {
            map.jumpTo({ center: [erstePunkt.lng, erstePunkt.lat], zoom: 14 });
          }
        });

        mapRef.current = map;
      })
      .catch((e) => console.error('Karte konnte nicht geladen werden:', e));

    return () => {
      abgebrochen = true;
      mapRef.current?.remove();
      mapRef.current = null;
      // Bugfix (beim Testen gefunden): react-dom meldet "Attempted to
      // synchronously unmount a root while React was already rendering",
      // wenn man die Marker-/Popup-Mini-Roots HIER direkt unmountet — dieser
      // Cleanup läuft mitten im Unmount/Re-Render der übergeordneten
      // Komponente. `queueMicrotask` reichte NICHT (React-Router verkettet
      // bei einer Client-Navigation mehrere synchrone Renderdurchgänge, ohne
      // dazwischen die Microtask-Queue abzuarbeiten) — `setTimeout` schiebt
      // den Unmount dagegen in einen echten neuen Makrotask, garantiert nach
      // Abschluss der aktuellen Renderkette.
      const alteMarkerRoots = markerRoots.current;
      const altePopupRoots = popupRoots.current;
      setTimeout(() => {
        for (const r of alteMarkerRoots) r.unmount();
        for (const r of altePopupRoots) r.unmount();
      }, 0);
      markerRoots.current = [];
      popupRoots.current = [];
    };
    // punkte bewusst nicht in den Deps — bei Änderung wird die ganze Karte
    // neu aufgebaut (Cleanup oben), das reicht für diesen Anwendungsfall.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punkte]);

  return (
    <div
      ref={containerRef}
      style={{ height: hoehe, borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--raspel)' }}
    />
  );
}

function PopupInhalt({ punkt, onAnsehen }: { punkt: KartenPunkt; onAnsehen?: () => void }) {
  return (
    <div style={{ minWidth: '10rem' }}>
      <strong style={{ fontFamily: 'var(--font-display)' }}>{punkt.name}</strong>
      <div style={{ color: 'var(--espresso-70)', fontSize: '0.85rem', marginBottom: '0.35rem' }}>{punkt.ort}</div>
      <GuennieRating wert={punkt.avgGesamtGuennies ?? null} groesse="sm" />
      <div className="label" style={{ marginTop: '0.25rem' }}>
        {punkt.anzahlTests} Verkostung{punkt.anzahlTests === 1 ? '' : 'en'}
      </div>
      {onAnsehen ? (
        <button type="button" className="btn btn-sekundaer" style={{ marginTop: '0.5rem', width: '100%' }} onClick={onAnsehen}>
          Ansehen →
        </button>
      ) : null}
    </div>
  );
}
